/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { getBottomNavItems, isNavActive } from './navItems';

/** Mobile bottom navigation (<lg). Extracted from AppShell.jsx (Code Quality refactor). */
export const BottomNav = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const items = getBottomNavItems(user);
  return (
    <nav
      data-testid="bottom-navigation"
      aria-label={t('a11yPrimaryNav', 'Navigazione principale')}
      className="fixed bottom-0 inset-x-0 z-30 lg:hidden bg-background/90 backdrop-blur border-t border-border rounded-t-2xl"
    >
      <div className="max-w-[720px] mx-auto flex items-center justify-around px-1 pr-[6.5rem] pb-[env(safe-area-inset-bottom)]">
        {items.map(({ key, path, labelKey, Icon }) => {
          const active = isNavActive(location.pathname, path);
          return (
            <button
              key={key} onClick={() => navigate(path)}
              data-testid={`bottom-navigation-item-${key}`}
              aria-label={t(labelKey)}
              aria-current={active ? 'page' : undefined}
              className="relative flex-1 min-h-[56px] flex flex-col items-center justify-center gap-0.5 py-2 group"
            >
              <motion.div
                animate={{ scale: active ? 1.06 : 1, y: active ? -1 : 0 }}
                transition={{ duration: 0.18 }}
                className={`p-1.5 rounded-full ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
              </motion.div>
              <span className={`text-[11px] font-medium ${active ? 'text-primary' : 'text-muted-foreground'}`}>{t(labelKey)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
